import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  DEFAULT_SIGMA_FRACTION,
  DEFAULT_VIDEO_WINDOW_MS,
  EXPLORATORY_WARNING,
  REFERENCE_WEBGAZER_LAG_MS,
  VIDEO_WINDOW_OPTIONS_MS,
  type AnalysisResultDto,
  type DataSource,
  type RoiDto,
  type RoiGeometry,
  type VideoGazeDto,
} from "@gazelab/shared";
import {
  coverageSeries,
  diffGrids,
  gridDimensions,
  gridMax,
  gridMaxAbs,
  groupDispersion,
  groupWindowHeatmap,
  roiTimeSeries,
  timeWindows,
  type Grid,
  type SeriesPoint,
  type VideoParticipant,
} from "@gazelab/analysis";
import { api, errorMessage } from "../../services/api.js";
import { useAsync } from "../../lib/useAsync.js";
import type { OverlaySpec } from "../../lib/heatmapCanvas.js";
import { fmtNum, fmtPct } from "../../lib/format.js";
import { Alert, ErrorAlert, Spinner } from "../../components/ui/index.js";
import { Icon } from "../../components/ui/Icon.js";
import { NoticeStrip, Segmented, Tabs } from "../../components/ui/Layout.js";
import { DivergingLegend, SequentialLegend } from "../../components/analysis/Legends.js";
import { ExclusionCompare, GroupCompare } from "../../components/analysis/GroupCompare.js";
import { VideoHeatmapPlayer } from "../../components/analysis/VideoHeatmapPlayer.js";
import { VideoTimeline } from "../../components/analysis/VideoTimeline.js";
import { useStudy } from "./StudyLayout.js";
import { useResultsShared } from "./resultsShared.js";
import { StimulusPicker } from "../../components/analysis/StimulusPicker.js";
import { DEMO_EXPORT_NOTE, useDemo } from "./AdminLayout.js";

type LagMode = "none" | "measured" | "fixed";
type MapMode = "A" | "B" | "diff";
type BottomTab = "rois" | "metrics" | "exclusions" | "warnings" | "method";

/** Rejilla de los mapas por ventana (más gruesa que en imágenes: se recalcula al reproducir). */
const VIDEO_GRID_LONG_SIDE = 96;

function ControlCard({ title, children, aside, tour }: { title: string; children: ReactNode; aside?: ReactNode; tour?: string }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm" aria-label={title} data-tour={tour}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="eyebrow">{title}</h2>
        {aside}
      </div>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

const fmtSec = (ms: number) => `${Number((ms / 1000).toFixed(1)).toLocaleString("es-MX")} s`;

/** Participantes con el retraso que corresponde a la corrección elegida. */
function withLag(data: VideoGazeDto["groupA"], mode: LagMode, fixedMs: number): VideoParticipant[] {
  return data.participants.map((p) => ({
    t: p.t,
    x: p.x,
    y: p.y,
    lagMs: mode === "none" ? 0 : mode === "fixed" ? fixedMs : (p.lagMs ?? fixedMs),
  }));
}

export function VideoResultsPage() {
  const { study } = useStudy();
  const exportsOff = useDemo()?.enabled === true;
  const shared = useResultsShared();
  const { stimulusId: videoId, groupA, setGroupA, groupB, setGroupB, source, setSource, includeWarn, setIncludeWarn } = shared;
  const groups = study.groups;
  const [windowMs, setWindowMs] = useState<number>(DEFAULT_VIDEO_WINDOW_MS);
  const [lagMode, setLagMode] = useState<LagMode>("none");
  const [fixedLag, setFixedLag] = useState(REFERENCE_WEBGAZER_LAG_MS);
  const [mapMode, setMapMode] = useState<MapMode>("A");
  const [sigma, setSigma] = useState(DEFAULT_SIGMA_FRACTION * 1.5);
  const [opacity, setOpacity] = useState(65);
  const [diffThreshold, setDiffThreshold] = useState(10);
  const [timeMs, setTimeMs] = useState(0);
  const [tab, setTab] = useState<BottomTab>("rois");
  const [drawMode, setDrawMode] = useState(false);
  const [roiLabel, setRoiLabel] = useState("");
  const [roiStart, setRoiStart] = useState<number | null>(null);
  const [roiEnd, setRoiEnd] = useState<number | null>(null);
  const [chartRoi, setChartRoi] = useState<string>("");
  const [hoverRoi, setHoverRoi] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const video = shared.current;
  const durationMs = video?.durationMs ?? 0;

  useEffect(() => {
    setTimeMs(0);
    setRoiStart(null);
    setRoiEnd(null);
    setChartRoi("");
  }, [videoId]);

  const canAnalyze = Boolean(videoId && groupA && groupB && groupA !== groupB);
  const gaze = useAsync<VideoGazeDto | null>(
    () => (canAnalyze ? api.videoGaze(study.id, { videoId, groupA, groupB, source, includeWarnQuality: includeWarn }) : Promise.resolve(null)),
    [study.id, videoId, groupA, groupB, source, includeWarn],
  );
  const rois = useAsync(() => (videoId ? api.listRois(study.id, videoId) : Promise.resolve([] as RoiDto[])), [study.id, videoId]);
  // Métricas del video completo (mismo análisis que en imágenes; los ROIs respetan su intervalo).
  const whole = useAsync<AnalysisResultDto | null>(
    () => (canAnalyze ? api.analysis(study.id, { imageId: videoId, groupA, groupB, source, includeWarnQuality: includeWarn }) : Promise.resolve(null)),
    [study.id, videoId, groupA, groupB, source, includeWarn, rois.data],
  );

  const g = gaze.data && gaze.data.video.id === videoId ? gaze.data : null;
  const partsA = useMemo(() => (g ? withLag(g.groupA, lagMode, fixedLag) : []), [g, lagMode, fixedLag]);
  const partsB = useMemo(() => (g ? withLag(g.groupB, lagMode, fixedLag) : []), [g, lagMode, fixedLag]);
  const windows = useMemo(() => timeWindows(durationMs, windowMs), [durationMs, windowMs]);
  /** Mínimo de muestras para que un participante cuente en una ventana (~5 por segundo). */
  const minSamples = Math.max(3, Math.round(windowMs / 200));
  const windowIdx = Math.min(windows.length - 1, Math.max(0, Math.floor(timeMs / windowMs)));
  const win = windows[windowIdx] ?? null;
  const dims = useMemo(() => (video ? gridDimensions(video.naturalWidth, video.naturalHeight, VIDEO_GRID_LONG_SIDE) : { width: 16, height: 9 }), [video]);

  // Mapas por ventana: se calculan al llegar a cada ventana y se guardan mientras no cambien los parámetros.
  const cache = useMemo(() => {
    // Caché nueva cada vez que cambian los datos o los parámetros que definen los mapas.
    void [partsA, partsB, windowMs, sigma, dims];
    return new Map<number, { a: Grid | null; b: Grid | null; nA: number; nB: number }>();
  }, [partsA, partsB, windowMs, sigma, dims]);
  const current = useMemo(() => {
    if (!win || !g) return null;
    let hit = cache.get(win.index);
    if (!hit) {
      const opts = { ...dims, sigmaFraction: sigma, minSamples };
      const a = groupWindowHeatmap(partsA, win.startMs, win.endMs, opts);
      const b = groupWindowHeatmap(partsB, win.startMs, win.endMs, opts);
      hit = { a: a.grid, b: b.grid, nA: a.n, nB: b.n };
      cache.set(win.index, hit);
    }
    return hit;
  }, [cache, win, g, partsA, partsB, dims, sigma, minSamples]);

  const overlay: OverlaySpec | null = useMemo(() => {
    if (!current) return null;
    const toDto = (grid: Grid) => ({ width: grid.width, height: grid.height, values: Array.from(grid.values), max: gridMax(grid) });
    if (mapMode === "diff") {
      if (!current.a || !current.b) return null;
      const d = diffGrids(current.a, current.b);
      return { grid: toDto(d), mode: "diverging", max: gridMaxAbs(d), opacity: opacity / 100, threshold: diffThreshold / 100 };
    }
    const grid = mapMode === "A" ? current.a : current.b;
    if (!grid) return null;
    const shared = Math.max(current.a ? gridMax(current.a) : 0, current.b ? gridMax(current.b) : 0);
    return { grid: toDto(grid), mode: "sequential", max: shared, opacity: opacity / 100, threshold: 0.02 };
  }, [current, mapMode, opacity, diffThreshold]);

  const roiList = rois.data ?? [];
  const selectedRoi = roiList.find((r) => r.id === chartRoi) ?? null;
  const series = useMemo((): { points: [SeriesPoint[], SeriesPoint[]]; yMax: number; label: string; fmt: (v: number) => string } => {
    if (selectedRoi) {
      const roi = { ...selectedRoi.geometry, startMs: selectedRoi.startMs, endMs: selectedRoi.endMs };
      return {
        points: [roiTimeSeries(partsA, roi, windows, minSamples), roiTimeSeries(partsB, roi, windows, minSamples)],
        yMax: 1,
        label: `% de mirada en «${selectedRoi.label}»`,
        fmt: (v) => fmtPct(v, 0),
      };
    }
    const disp = (parts: VideoParticipant[]): SeriesPoint[] =>
      windows.map((w) => {
        const d = groupDispersion(parts, w.startMs, w.endMs, minSamples);
        return { startMs: w.startMs, endMs: w.endMs, n: 0, mean: d, ciLow: null, ciHigh: null };
      });
    return { points: [disp(partsA), disp(partsB)], yMax: 0.5, label: "Dispersión de la mirada", fmt: (v) => fmtNum(v, 2) };
  }, [selectedRoi, partsA, partsB, windows, minSamples]);
  const coverage = useMemo(
    () => [
      { key: "A" as const, values: coverageSeries(partsA, windows, minSamples) },
      { key: "B" as const, values: coverageSeries(partsB, windows, minSamples) },
    ],
    [partsA, partsB, windows, minSamples],
  );

  const seek = useCallback((ms: number) => {
    const v = videoRef.current;
    if (v) v.currentTime = ms / 1000;
    setTimeMs(ms);
  }, []);
  // Actualizar el tiempo como mucho cada 100 ms (el mapa solo cambia al cambiar de ventana).
  const lastTick = useRef(0);
  const onTime = useCallback((ms: number) => {
    const now = performance.now();
    if (now - lastTick.current < 100) return;
    lastTick.current = now;
    setTimeMs(ms);
  }, []);

  const activeRois = roiList.filter((r) => (r.startMs === null || timeMs >= r.startMs) && (r.endMs === null || timeMs < r.endMs));

  async function addRoi(geometry: RoiGeometry) {
    setError(null);
    try {
      const startMs = roiStart ?? win?.startMs ?? 0;
      const endMs = roiEnd ?? Math.min(durationMs, startMs + 5000);
      const created = await api.createRoi(study.id, {
        studyImageId: videoId,
        label: roiLabel.trim() || `ROI ${roiList.length + 1}`,
        shape: "rect",
        geometry,
        startMs: Math.round(startMs),
        endMs: endMs > startMs ? Math.round(endMs) : null,
      });
      setRoiLabel("");
      setDrawMode(false);
      setChartRoi(created.id);
      setTab("rois");
      await rois.reload();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  async function roiAction(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await rois.reload();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  if (groups.length < 2) return <Alert tone="warning">El estudio necesita al menos dos grupos de edad para comparar.</Alert>;
  const groupName = (id: string) => groups.find((x) => x.id === id)?.name ?? "";
  const nTotal = g ? { a: g.groupA.n, b: g.groupB.n } : null;
  const warnings = [...new Set([...(g?.warnings ?? []), ...(whole.data?.warnings ?? [])])];

  return (
    <div className="grid gap-4 xl:grid-cols-[17.5rem_minmax(0,1fr)]">
      <aside className="space-y-3 xl:sticky xl:top-5 xl:max-h-[calc(100vh-2.5rem)] xl:self-start xl:overflow-y-auto xl:pb-1" aria-label="Controles del análisis de video">
        <ControlCard tour="res-data" title="Datos">
          <label htmlFor="vr-source" className="sr-only">
            Fuente de datos
          </label>
          <select id="vr-source" className="input" value={source} onChange={(e) => setSource(e.target.value as DataSource)} data-testid="source-select">
            <option value="webgazer">Reales (webcam)</option>
            <option value="synthetic">SINTÉTICA (demo)</option>
          </select>
          <label htmlFor="vr-warn" className="flex items-center gap-2 text-sm text-slate-700">
            <input id="vr-warn" type="checkbox" checked={includeWarn} onChange={(e) => setIncludeWarn(e.target.checked)} />
            Incluir validaciones con advertencia
          </label>
        </ControlCard>

        <ControlCard tour="res-stimulus" title="Estímulo" aside={shared.stimuli.length > 0 && <span className="font-util text-xs text-slate-500">{shared.stimuli.findIndex((v) => v.id === videoId) + 1} / {shared.stimuli.length}</span>}>
          <StimulusPicker studyId={study.id} stimuli={shared.stimuli} value={videoId} onChange={shared.setStimulusId} />
        </ControlCard>

        <ControlCard
          tour="res-compare"
          title="Comparar"
          aside={
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold text-sky-800 hover:bg-sky-50"
              onClick={() => {
                setGroupA(groupB);
                setGroupB(groupA);
              }}
            >
              <Icon name="swap" className="h-3.5 w-3.5" /> Intercambiar
            </button>
          }
        >
          {(
            [
              ["vr-a", "A", groupA, setGroupA, "bg-sky-700"],
              ["vr-b", "B", groupB, setGroupB, "bg-amber-600"],
            ] as const
          ).map(([id, label, value, set, color]) => (
            <div key={id} className="flex items-center gap-2">
              <label htmlFor={id} className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${color}`}>
                <span className="sr-only">Grupo </span>
                {label}
              </label>
              <select id={id} className="input py-1.5" value={value} onChange={(e) => set(e.target.value)}>
                {groups.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </div>
          ))}
          {groupA === groupB && <p className="text-xs text-amber-700">Elige dos grupos distintos.</p>}
        </ControlCard>

        <ControlCard tour="res-time" title="Tiempo">
          <div>
            <span className="mb-1 block text-xs text-slate-600">Ventana de análisis</span>
            <Segmented
              label="Ventana de análisis"
              size="sm"
              value={String(windowMs)}
              onChange={(v) => setWindowMs(Number(v))}
              options={VIDEO_WINDOW_OPTIONS_MS.map((w) => ({ value: String(w), label: `${w / 1000} s` }))}
            />
          </div>
          <div>
            <label htmlFor="vr-lag" className="mb-1 block text-xs text-slate-600">
              Corrección de latencia
            </label>
            <select id="vr-lag" className="input py-1.5" value={lagMode} onChange={(e) => setLagMode(e.target.value as LagMode)}>
              <option value="none">Sin corrección</option>
              <option value="measured">Medida por participante</option>
              <option value="fixed">Fija</option>
            </select>
          </div>
          {lagMode !== "none" && (
            <div>
              <label htmlFor="vr-lagms" className="flex justify-between text-xs text-slate-600">
                <span>{lagMode === "fixed" ? "Retraso fijo" : "Respaldo si no se midió"}</span>
                <span className="font-util">{fixedLag} ms</span>
              </label>
              <input id="vr-lagms" type="range" min={0} max={800} step={10} value={fixedLag} onChange={(e) => setFixedLag(Number(e.target.value))} className="w-full" />
            </div>
          )}
          <p className="text-xs text-slate-500">La mirada llega con retraso (~300 ms con webcam): la corrección asigna cada muestra al momento que se estaba viendo.</p>
        </ControlCard>

        <ControlCard tour="res-map" title="Mapa">
          <Segmented
            label="Mapa mostrado"
            value={mapMode}
            onChange={setMapMode}
            options={[
              { value: "A", label: "A" },
              { value: "B", label: "B" },
              { value: "diff", label: "A − B" },
            ]}
          />
          <div>
            <label htmlFor="vr-sigma" className="flex justify-between text-xs text-slate-600">
              <span>Suavizado σ</span>
              <span className="font-util">{(sigma * 100).toFixed(1)} %</span>
            </label>
            <input id="vr-sigma" type="range" min={0.01} max={0.1} step={0.005} value={sigma} onChange={(e) => setSigma(Number(e.target.value))} className="w-full" />
          </div>
          <div>
            <label htmlFor="vr-opacity" className="flex justify-between text-xs text-slate-600">
              <span>Opacidad</span>
              <span className="font-util">{opacity} %</span>
            </label>
            <input id="vr-opacity" type="range" min={0} max={100} step={5} value={opacity} onChange={(e) => setOpacity(Number(e.target.value))} className="w-full" />
          </div>
          {mapMode === "diff" && (
            <div>
              <label htmlFor="vr-thr" className="flex justify-between text-xs text-slate-600">
                <span>Umbral visible de diferencia</span>
                <span className="font-util">{diffThreshold} %</span>
              </label>
              <input id="vr-thr" type="range" min={0} max={50} step={1} value={diffThreshold} onChange={(e) => setDiffThreshold(Number(e.target.value))} className="w-full" />
            </div>
          )}
        </ControlCard>

        <ControlCard tour="res-roi" title="Nuevo ROI">
          <input aria-label="Etiqueta para el próximo ROI" placeholder="Etiqueta (opcional)" className="input py-1.5" value={roiLabel} onChange={(e) => setRoiLabel(e.target.value)} maxLength={80} />
          <div className="grid grid-cols-2 gap-1.5 text-xs">
            <button type="button" className="btn-secondary px-2 py-1.5 text-xs" onClick={() => setRoiStart(timeMs)}>
              Inicio: {fmtSec(roiStart ?? win?.startMs ?? 0)}
            </button>
            <button type="button" className="btn-secondary px-2 py-1.5 text-xs" onClick={() => setRoiEnd(timeMs)}>
              Fin: {fmtSec(roiEnd ?? Math.min(durationMs, (roiStart ?? win?.startMs ?? 0) + 5000))}
            </button>
          </div>
          <p className="text-xs text-slate-500">Lleva el video al momento deseado y pulsa «Inicio» o «Fin» para fijarlo (por defecto, 5 s desde la ventana actual).</p>
          <button type="button" aria-pressed={drawMode} className={`w-full ${drawMode ? "btn-primary" : "btn-secondary"}`} onClick={() => setDrawMode((d) => !d)} disabled={!video}>
            <Icon name="square" className="h-4 w-4" />
            {drawMode ? "Dibujando… (clic para salir)" : "Dibujar ROI sobre el video"}
          </button>
        </ControlCard>

        <ControlCard tour="res-export" title="Exportar">
          <a
            className={`btn-secondary w-full justify-center text-xs ${videoId && !exportsOff ? "" : "pointer-events-none opacity-50"}`}
            aria-disabled={exportsOff || !videoId}
            title={exportsOff ? DEMO_EXPORT_NOTE : undefined}
            href={api.exportUrl(study.id, "windows.csv", { source, imageId: videoId, windowMs, lagMode, lagMs: fixedLag })}
            download
          >
            <Icon name="download" className="h-4 w-4" /> windows.csv (estos ajustes)
          </a>
        </ControlCard>
      </aside>

      <div className="min-w-0 space-y-3">
        {source === "synthetic" && (
          <div className="flex items-center gap-3 rounded-xl bg-fuchsia-50 px-3 py-2 ring-2 ring-fuchsia-500" data-testid="synthetic-results-badge" role="note">
            <span className="rounded-md bg-fuchsia-700 px-2.5 py-0.5 text-sm font-black tracking-widest text-white">SINTÉTICO</span>
            <span className="text-sm text-fuchsia-950">Datos de demostración generados por computadora. No provienen de personas y nunca se combinan con datos reales.</span>
          </div>
        )}
        {source === "webgazer" && study.sessionCounts.synthetic.total > 0 && study.sessionCounts.webgazer.completed === 0 && (
          <NoticeStrip tone="info">
            Aún no hay sesiones reales completadas. Para ver la demostración elige la fuente <strong>«SINTÉTICA (demo)»</strong>.
          </NoticeStrip>
        )}
        <ErrorAlert error={error ?? gaze.error ?? shared.error ?? rois.error} />

        {video && (
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" aria-label="Video con mapa de calor" data-tour="res-player">
            <div className="mb-3 grid items-start gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
              <div className="min-w-0">
                <h2 className="truncate text-base font-bold text-ink">
                  {mapMode === "diff" ? "Diferencia A − B" : `${mapMode}: ${groupName(mapMode === "A" ? groupA : groupB)}`} · ventana {fmtSec(win?.startMs ?? 0)}–{fmtSec(win?.endMs ?? 0)}
                  {gaze.loading && <span className="ml-2 text-sm font-normal text-sky-700">Cargando…</span>}
                </h2>
                <p className="mt-0.5 text-xs text-slate-600" data-testid="window-n">
                  En esta ventana: nA = {current?.nA ?? 0} de {nTotal?.a ?? "…"}, nB = {current?.nB ?? 0} de {nTotal?.b ?? "…"} participantes con datos
                  {lagMode !== "none" && ` · latencia corregida (${lagMode === "fixed" ? `${fixedLag} ms` : "medida"})`}
                </p>
                <p className="mt-0.5 flex items-start gap-1.5 text-xs text-amber-800">
                  <Icon name="alert" className="mt-px h-3.5 w-3.5 shrink-0" />
                  <span>{EXPLORATORY_WARNING}</span>
                </p>
              </div>
              <div className="w-60">
                {mapMode === "diff" ? <DivergingLegend maxAbs={overlay?.max ?? 0} threshold={diffThreshold / 100} groupA="A" groupB="B" /> : <SequentialLegend max={overlay?.max ?? 0} />}
              </div>
            </div>
            <VideoHeatmapPlayer
              src={api.adminImageUrl(study.id, video.id)}
              naturalWidth={video.naturalWidth}
              naturalHeight={video.naturalHeight}
              overlay={overlay}
              rois={activeRois}
              highlightRoiId={hoverRoi ?? chartRoi}
              drawMode={drawMode}
              onRoiDrawn={addRoi}
              onTime={onTime}
              videoRef={videoRef}
              maxHeight={Math.max(240, window.innerHeight - 520)}
            />
            {drawMode && <p className="mt-2 text-center text-xs text-slate-600">Video en pausa: arrastra sobre el fotograma para dibujar el ROI ({fmtSec(roiStart ?? win?.startMs ?? 0)} – {fmtSec(roiEnd ?? Math.min(durationMs, (roiStart ?? win?.startMs ?? 0) + 5000))}).</p>}
          </section>
        )}

        {video && (
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" aria-label="Evolución en el tiempo" data-tour="res-timeline">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-bold text-ink">{series.label} por ventana</h2>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                Mostrar
                <select className="input w-auto py-1" value={chartRoi} onChange={(e) => setChartRoi(e.target.value)} data-testid="chart-metric">
                  <option value="">Dispersión de la mirada</option>
                  {roiList.map((r) => (
                    <option key={r.id} value={r.id}>
                      ROI: {r.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {gaze.loading && !g ? (
              <Spinner />
            ) : (
              <VideoTimeline
                testId="video-timeline"
                durationMs={durationMs}
                series={[
                  { key: "A", label: g?.groupA.groupName ?? groupName(groupA), points: series.points[0] },
                  { key: "B", label: g?.groupB.groupName ?? groupName(groupB), points: series.points[1] },
                ]}
                coverage={coverage}
                timeMs={timeMs}
                yLabel={series.label}
                yMax={series.yMax}
                formatY={series.fmt}
                highlight={selectedRoi ? { startMs: selectedRoi.startMs, endMs: selectedRoi.endMs } : null}
                onSeek={seek}
              />
            )}
            <p className="mt-1 text-xs text-slate-500">
              {selectedRoi
                ? "Fracción de las muestras de cada participante que cae en el ROI, promediada por grupo (cada persona pesa igual); banda = IC 95 % aproximado. Fuera del intervalo del ROI no hay valor."
                : "Distancia media de la mirada de cada participante al centro del grupo (unidades del ancho/alto del video). Más baja = el grupo mira al mismo sitio."}{" "}
              Las barras inferiores indican cuántos participantes tienen datos en cada ventana.
            </p>
          </section>
        )}

        {video && (
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm" aria-label="Detalle del análisis" data-tour="res-tabs">
            <div className="px-3 pt-1">
              <Tabs
                label="Detalle del análisis de video"
                value={tab}
                onChange={setTab}
                tabs={[
                  { value: "rois", label: "Regiones de interés", badge: roiList.length },
                  { value: "metrics", label: "Video completo A vs B" },
                  { value: "exclusions", label: "Exclusiones" },
                  { value: "warnings", label: "Advertencias", badge: warnings.length },
                  { value: "method", label: "Método" },
                ]}
              />
            </div>
            <div className="p-3">
              {tab === "rois" && (
                <VideoRoiTable
                  rois={roiList}
                  analysis={whole.data ?? null}
                  timeMs={timeMs}
                  selected={chartRoi}
                  onSelect={(id) => setChartRoi(id)}
                  onHover={setHoverRoi}
                  onSeek={seek}
                  onPatch={(r, patch) => roiAction(() => api.updateRoi(study.id, r.id, patch))}
                  onDelete={(r) => roiAction(() => api.deleteRoi(study.id, r.id))}
                />
              )}
              {tab === "metrics" && (whole.data ? <GroupCompare a={whole.data.groupA} b={whole.data.groupB} /> : <Spinner />)}
              {tab === "exclusions" && (whole.data ? <ExclusionCompare a={whole.data.groupA} b={whole.data.groupB} /> : <Spinner />)}
              {tab === "warnings" && (
                <ul className="space-y-1.5 px-1 text-sm text-amber-950">
                  {warnings.map((w) => (
                    <li key={w} className="flex items-start gap-2">
                      <Icon name="alert" className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                      {w}
                    </li>
                  ))}
                  {warnings.length === 0 && <li className="text-slate-500">Sin advertencias.</li>}
                </ul>
              )}
              {tab === "method" && (
                <div className="max-w-3xl space-y-2 px-1 text-sm text-slate-600">
                  <p>
                    Cada muestra de mirada tiene el tiempo del fotograma que estaba en pantalla (registrado en el navegador con requestVideoFrameCallback). El video se divide en ventanas de{" "}
                    {windowMs / 1000} s; en cada una, cada participante con al menos {minSamples} muestras aporta un mapa normalizado y el mapa del grupo es el promedio (cada persona pesa igual).
                  </p>
                  <p>
                    WebGazer estima la mirada con un retraso de cientos de milisegundos. La corrección de latencia resta ese retraso (fijo o medido para cada persona con el punto en
                    movimiento) antes de asignar la muestra a una ventana. Usa ventanas de 0,5 s o más y no interpretes diferencias de tiempo menores a ~300 ms.
                  </p>
                  <p>Las métricas del «video completo» usan todas las muestras del video; un ROI con intervalo solo cuenta las muestras dentro de su intervalo.</p>
                  <p className="font-util text-xs text-slate-500">
                    Rejilla {dims.width}×{dims.height} · σ = {(sigma * 100).toFixed(1)} % del lado largo · ventana {windowMs} ms
                  </p>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function VideoRoiTable(props: {
  rois: RoiDto[];
  analysis: AnalysisResultDto | null;
  timeMs: number;
  selected: string;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
  onSeek: (ms: number) => void;
  onPatch: (roi: RoiDto, patch: { label?: string; startMs?: number | null; endMs?: number | null }) => void;
  onDelete: (roi: RoiDto) => void;
}) {
  const a = props.analysis;
  const frac = (list: AnalysisResultDto["groupA"]["rois"] | undefined, id: string) => list?.find((r) => r.roiId === id)?.meanFraction ?? null;
  if (props.rois.length === 0) {
    return <p className="px-1 text-sm text-slate-500">Sin ROIs. Usa «Dibujar ROI sobre el video» en el panel izquierdo: el ROI se aplica solo durante su intervalo.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm" data-testid="video-roi-table">
        <thead className="text-left text-xs uppercase text-slate-500">
          <tr>
            <th className="py-1 pr-3">ROI</th>
            <th className="py-1 pr-3">Intervalo</th>
            <th className="py-1 pr-3">{a ? `A: ${a.groupA.groupName}` : "Grupo A"}</th>
            <th className="py-1 pr-3">{a ? `B: ${a.groupB.groupName}` : "Grupo B"}</th>
            <th className="py-1 pr-3">A − B</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {props.rois.map((r) => {
            const fa = frac(a?.groupA.rois, r.id);
            const fb = frac(a?.groupB.rois, r.id);
            const diff = a?.roiDifferences.find((d) => d.roiId === r.id)?.diffPp ?? null;
            return (
              <tr
                key={r.id}
                className={`border-t border-slate-100 ${props.selected === r.id ? "bg-sky-50/60" : ""}`}
                onMouseEnter={() => props.onHover(r.id)}
                onMouseLeave={() => props.onHover(null)}
              >
                <td className="py-1 pr-3">
                  <button type="button" className="font-medium text-sky-800 hover:underline" onClick={() => props.onSelect(r.id)} title="Ver su curva en el tiempo">
                    {r.label}
                  </button>
                </td>
                <td className="whitespace-nowrap py-1 pr-3 font-util text-xs">
                  <button type="button" className="hover:underline" onClick={() => props.onSeek(r.startMs ?? 0)} title="Ir al inicio">
                    {fmtSec(r.startMs ?? 0)}
                  </button>{" "}
                  – {r.endMs === null ? "final" : fmtSec(r.endMs)}
                </td>
                <td className="py-1 pr-3">{fmtPct(fa)}</td>
                <td className="py-1 pr-3">{fmtPct(fb)}</td>
                <td className="py-1 pr-3 font-semibold">{diff === null ? "—" : `${diff > 0 ? "+" : ""}${fmtNum(diff, 1)} pp`}</td>
                <td className="whitespace-nowrap py-1 text-right">
                  <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={() => props.onPatch(r, { startMs: Math.round(props.timeMs) })} title="Usar el tiempo actual como inicio">
                    Inicio = ahora
                  </button>{" "}
                  <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={() => props.onPatch(r, { endMs: Math.round(props.timeMs) })} title="Usar el tiempo actual como fin">
                    Fin = ahora
                  </button>{" "}
                  <button
                    type="button"
                    className="btn-secondary px-2 py-1 text-xs"
                    onClick={() => {
                      const label = window.prompt("Nueva etiqueta", r.label)?.trim();
                      if (label) props.onPatch(r, { label });
                    }}
                  >
                    Renombrar
                  </button>{" "}
                  <button type="button" className="btn-danger px-2 py-1 text-xs" onClick={() => props.onDelete(r)}>
                    Eliminar
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="mt-1 text-xs text-slate-500">
        Porcentajes del video completo dentro del intervalo de cada ROI: fracción de las muestras de cada participante en el ROI, promediada por grupo. Elige un ROI para ver su evolución en el tiempo.
      </p>
    </div>
  );
}
